import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Help as HelpIcon,
  Refresh as RefreshIcon,

  AccessTime as AccessTimeIcon,
  Memory as MemoryIcon,
  Cloud as CloudIcon,
  CloudQueue as StagingIcon,
  Code as DevelopmentIcon
} from '@mui/icons-material';
import { useServiceTesting } from '../../hooks/useServiceTesting';
import { ENVIRONMENTS, getServiceConfig } from '../../constants/serviceEndpoints';

const EnvironmentServiceHealthCheck = ({ selectedService }) => {
  const [healthStatus, setHealthStatus] = useState({});
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const { checkServiceHealth } = useServiceTesting();

  const environments = useMemo(() => [
    { key: ENVIRONMENTS.DEVELOPMENT, name: 'Development', icon: <DevelopmentIcon />, color: 'success' },
    { key: ENVIRONMENTS.STAGING, name: 'Staging', icon: <StagingIcon />, color: 'primary' },
    { key: ENVIRONMENTS.PRODUCTION, name: 'Production', icon: <CloudIcon />, color: 'error' }
  ], []);



  const handleCheckAllEnvironments = useCallback(async () => {
    if (!selectedService) return;

    setIsChecking(true);
    try {
      // Check the service in all environments
      const results = {};
      for (const env of environments) {
        try {
          const result = await checkServiceHealth(selectedService, env.key);
          results[env.key] = result;
        } catch (error) {
          results[env.key] = {
            status: 'unhealthy',
            error: error.message,
            timestamp: new Date().toISOString(),
            environment: env.key
          };
        }
      }
      setHealthStatus(results);
      setLastChecked(new Date());
    } catch (error) {
      console.error('Failed to check all environments:', error);
    } finally {
      setIsChecking(false);
    }
  }, [selectedService, checkServiceHealth, environments]);

  useEffect(() => {
    if (selectedService) {
      // Always load both environments on mount or service change
      handleCheckAllEnvironments();
    }
    // Only run when selectedService changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  const getStatusColor = (status) => {
    const colorMap = {
      healthy: 'success',
      degraded: 'warning',
      unhealthy: 'error',
      unknown: 'default'
    };
    return colorMap[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      healthy: <CheckCircleIcon />,
      degraded: <WarningIcon />,
      unhealthy: <ErrorIcon />,
      unknown: <HelpIcon />
    };
    return iconMap[status] || <HelpIcon />;
  };

  const formatResponseTime = (ms) => {
    if (!ms) return 'N/A';
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  const renderCompactEnvironmentHealth = (env, health) => {
    if (!health) {
      return (
        <Paper key={env.key} sx={{ p: 1.5, border: `1px solid ${env.color}`, opacity: 0.6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {env.icon}
            <Typography variant="subtitle2" color={env.color}>
              {env.name}
            </Typography>
            <Chip label="No data" color="default" size="small" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Click refresh to check
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper 
        key={env.key} 
        sx={{ 
          p: 1.5, 
          border: `2px solid ${getStatusColor(health.status) === 'success' ? '#4caf50' : getStatusColor(health.status) === 'error' ? '#f44336' : '#ff9800'}`,
          bgcolor: getStatusColor(health.status) === 'success' ? 'success.light' : 
                   getStatusColor(health.status) === 'error' ? 'error.light' : 'warning.light',
          opacity: getStatusColor(health.status) === 'success' ? 0.9 : 0.8
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {env.icon}
            <Typography variant="subtitle2" color={env.color} sx={{ fontWeight: 600 }}>
              {env.name}
            </Typography>
          </Box>
          <Chip
            label={health.status || 'unknown'}
            color={getStatusColor(health.status)}
            size="small"
            icon={getStatusIcon(health.status)}
          />
        </Box>

        {/* Status Details */}
        {health.error ? (
          <Alert severity="error" sx={{ py: 0.5, px: 1, mb: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
            {health.error.length > 60 ? `${health.error.substring(0, 60)}...` : health.error}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {health.details?.response_time && (
              <Chip 
                label={`${formatResponseTime(health.details.response_time)}`} 
                size="small" 
                variant="outlined"
                icon={<AccessTimeIcon fontSize="small" />}
              />
            )}
            
            {health.details?.database && (
              <Chip 
                label={`DB: ${health.details.database}`} 
                size="small" 
                variant="outlined"
                color={health.details.database === 'healthy' ? 'success' : 'warning'}
                icon={<MemoryIcon fontSize="small" />}
              />
            )}

            {health.details?.active_jobs !== undefined && (
              <Chip 
                label={`Jobs: ${health.details.active_jobs}`} 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Timestamp */}
        {health.details?.timestamp && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {new Date(health.details.timestamp).toLocaleTimeString()}
          </Typography>
        )}
      </Paper>
    );
  };

  if (!selectedService) {
    return (
      <Alert severity="info">
        Select a service to view health status
      </Alert>
    );
  }

  return (
    <Box>
      {/* Check if this is a backend service */}
      {getServiceConfig(selectedService)?.service_type === 'backend' ? (
        /* Backend Service - Simplified View */
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              Backend Service: {selectedService}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Check backend service health">
                <span>
                  <IconButton 
                    onClick={async () => {
                      setIsChecking(true);
                      try {
                        const result = await checkServiceHealth(selectedService);
                        setHealthStatus({ backend: result });
                        setLastChecked(new Date());
                      } catch (error) {
                        setHealthStatus({ 
                          backend: { 
                            status: 'unhealthy', 
                            error: error.message 
                          } 
                        });
                      } finally {
                        setIsChecking(false);
                      }
                    }}
                    disabled={isChecking}
                    color="primary"
                    size="small"
                  >
                    {isChecking ? <CircularProgress size={16} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              {lastChecked && (
                <Typography variant="caption" color="text.secondary">
                  {lastChecked.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Backend Health Status */}
          {healthStatus.backend && (
            <Paper sx={{ p: 2, bgcolor: 'success.light', opacity: 0.9 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Backend Service Health
                </Typography>
                <Chip
                  label={healthStatus.backend.status || 'unknown'}
                  color={getStatusColor(healthStatus.backend.status)}
                  size="small"
                  icon={getStatusIcon(healthStatus.backend.status)}
                />
              </Box>
              {healthStatus.backend.error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Error: {healthStatus.backend.error}
                </Typography>
              )}
            </Paper>
          )}

          <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Backend services run within the main application - no environment separation needed
            </Typography>
          </Box>
        </Box>
      ) : (
        /* Cloud Service - Environment Comparison View */
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              Service Health: {selectedService}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Refresh health status for both environments">
                <span>
                  <IconButton 
                    onClick={handleCheckAllEnvironments} 
                    disabled={isChecking}
                    color="primary"
                    size="small"
                  >
                    {isChecking ? <CircularProgress size={16} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              {lastChecked && (
                <Typography variant="caption" color="text.secondary">
                  {lastChecked.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Side-by-Side Environment Comparison */}
          <Grid container spacing={2}>
            {environments.map((env) => {
              const health = healthStatus[env.key];
              return (
                <Grid item xs={12} md={6} key={env.key}>
                  {renderCompactEnvironmentHealth(env, health)}
                </Grid>
              );
            })}
          </Grid>

          {/* Status Summary */}
          {Object.keys(healthStatus).length > 0 && (
            <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Environment Comparison: {Object.values(healthStatus).filter(h => h?.status === 'healthy').length}/{environments.length} environments healthy
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EnvironmentServiceHealthCheck; 